import React, { useState, useEffect } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';
import TicketCard from './TicketCard';
import SmsConversation from './SmsConversation';
import { useLineItemEditor } from './LineItemEditor';
import EffortPanel from './EffortPanel';
import RemoveTicket, { canRemoveTickets } from './RemoveTicket';
import TicketQuotes from './TicketQuotes';
import TicketFeedback from './TicketFeedback';
import EstimateSend from './EstimateSend';
import { RequestEstimateDialog, EstimateRequestBanner } from './EstimateRequest';
import { toast } from '../utils/toast';

function fmtPhone(p) {
  if (!p) return '';
  const digits = String(p).replace(/\D/g, '').replace(/^1/, '');
  if (digits.length !== 10) return p;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

function TicketDetail({ ticketId, ticket = null, ticketFlags, onClose, onTicketUpdated, onRemoved, onPrev, onNext, currentIndex, totalCount, theme = 'dark', currentUser = null }) {
  const [removing, setRemoving] = useState(false);
  const isDark = theme === 'dark';
  const T = getTheme(theme);

  // ── State ──────────────────────────────────────────────────────────────
  const [detail, setDetail] = useState(null);
  // Send estimate (26 Oct): the panel, and a nudge for the ticket's quote list.
  const [estimating, setEstimating] = useState(false);
  const [estimateSent, setEstimateSent] = useState(null);
  const [quotesKey, setQuotesKey] = useState(0);
  // Request estimate (26 Oct): techs ask, Managers and Admins send.
  const [requesting, setRequesting] = useState(false);
  const [requestDone, setRequestDone] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editingNote, setEditingNote] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // ── Data loading ───────────────────────────────────────────────────────
  const loadTicketDetail = () => {
    return fetch(`${API}/api/tickets/${ticketId}`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => { setDetail(data); return data; });
  };

  // Adding and editing line items: shared with the bench screen
  // (LineItemEditor.jsx). It settles its own pending totals and resets itself
  // when the ticket changes.
  const {
    addItemPanel, lineEditorModal, updateItemQuantity, deleteLineItem, openLineEditor,
    lineStock, totalsPending, employees,
  } = useLineItemEditor({ ticketId, detail, setDetail, reload: loadTicketDetail, theme });

  // Separate from ticket detail: this comes from Spinboard's own Postgres, not
  // Lightspeed, and failing to load it must never block the ticket rendering.
  // Defaults to [] rather than null on error so the rail says "None requested"
  // instead of vanishing.
  const loadPayments = () => {
    return fetch(`${API}/api/payments/list?workorderId=${encodeURIComponent(ticketId)}`, {
      credentials:'include',
      headers: { Authorization: `Bearer ${localStorage.getItem('sb_token')}` },
    })
      .then(r => r.ok ? r.json() : { requests: [] })
      .then(d => setPayments(d.requests || []))
      .catch(() => setPayments([]));
  };

  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    setDetail(null);
    setEstimateSent(null);
    setEstimating(false);
    setRequesting(false);
    setRequestDone(null);
    setPayments(null);
    setEditingNote(false);
    setNoteInput('');
    loadTicketDetail().finally(() => setLoading(false));
    loadPayments();
  }, [ticketId]);

  // Keyboard navigation — but not while typing in a note/reply field
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); onPrev && onPrev(); }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); onNext && onNext(); }
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  const [statusMeta, setStatusMeta] = useState([]);
  const [savingStatus, setSavingStatus] = useState(false);
  const [editingReceiptNote, setEditingReceiptNote] = useState(false);
  const [receiptNoteInput, setReceiptNoteInput] = useState('');
  const [savingReceiptNote, setSavingReceiptNote] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/tickets/status-meta`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setStatusMeta(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Statuses this user may set. Terminal and pickup-declaring statuses are
  // owner/manager only — the Manager Review Wizard is the gate before a
  // customer is told their bike is ready, and a tech finishing their own work
  // would bypass it. The server enforces this too; this only keeps the UI
  // from offering something that would come back 403.
  const FINISH_NAMES = ['finished', 'finished/paid', 'invoiced'];
  const canFinish = currentUser?.permissions?.finish_ticket !== false;
  const statusOptions = statusMeta
    .filter(s => {
      const n = (s.name || '').toLowerCase();
      if (n.includes('done') && n.includes('paid')) return false;
      if (!canFinish && FINISH_NAMES.includes(n)) return false;
      return true;
    })
    .map(s => ({ id: String(s.id ?? s.workorderStatusID), name: s.name, color: s.htmlColor }));

  const currentStatusId = statusMeta.find(s => s.name === detail?.status)?.workorderStatusID;

  // Edit in place rather than append: this text prints on the customer's
  // receipt, so a note assembled from stacked fragments would read badly.
  // The internal note next door stays append-only — different audience.
  // Both return true on success so LsEditField knows whether to flash green
  // or roll the draft back to the stored value.
  async function saveBikeField(patch) {
    const b = detail?.bike || {};
    try {
      const res = await fetch(`${API}/api/tickets/${ticketId}/bike`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify({
          serializedID: b.id,
          description: b.description === '—' ? '' : b.description,
          color: b.color, size: b.size, serial: b.serial,
          ...patch,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        window.alert(d.error || 'Could not save that change.');
        return false;
      }
      await loadTicketDetail();
      return true;
    } catch (_) {
      window.alert('Could not save that change.');
      return false;
    }
  }

  // The customer's bikes, for the Customer Item drop-down (7 Oct): pick one of
  // their earlier bikes, as Lightspeed lets you. Re-read when the ticket's bike
  // changes, so a bike just described here joins the list.
  const [customerBikes, setCustomerBikes] = useState(null);
  const bikeCustomerId = detail?.customer?.id && String(detail.customer.id) !== '0' ? String(detail.customer.id) : '';
  const ticketBikeId = detail?.bike?.id ? String(detail.bike.id) : '';
  useEffect(() => {
    if (!bikeCustomerId) { setCustomerBikes(null); return; }
    let live = true;
    fetch(`${API}/api/tickets/customer-bikes/${bikeCustomerId}`, { credentials:'include', headers: authHeaders() })
      .then(r => r.ok ? r.json() : { bikes: null })
      .then(d => { if (live) setCustomerBikes(Array.isArray(d.bikes) ? d.bikes : null); })
      .catch(() => { if (live) setCustomerBikes(null); });
    return () => { live = false; };
  }, [bikeCustomerId, ticketBikeId]);

  // Point the ticket at one of the customer's bikes, or at none ('' = a new
  // bike, described in the fields below). The server checks the bike is this
  // customer's and reads the ticket back before saying it changed.
  async function selectBike(serializedID) {
    try {
      const res = await fetch(`${API}/api/tickets/${ticketId}/bike/select`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify({ serializedID: serializedID || null }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        window.alert(d.error || 'Could not change the bike on this ticket.');
        return false;
      }
      const fresh = await loadTicketDetail();
      const name = fresh?.bike?.description && fresh.bike.description !== '—' ? fresh.bike.description : '';
      onTicketUpdated?.(ticketId, { bike: name });
      return true;
    } catch (_) {
      window.alert('Could not change the bike on this ticket.');
      return false;
    }
  }

  async function saveTicketFields(patch) {
    try {
      const res = await fetch(`${API}/api/tickets/${ticketId}/fields`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        window.alert(d.error || 'Could not save that change.');
        return false;
      }
      await loadTicketDetail();
      return true;
    } catch (_) {
      window.alert('Could not save that change.');
      return false;
    }
  }

  async function saveReceiptNote() {
    setSavingReceiptNote(true);
    try {
      const res = await fetch(`${API}/api/tickets/${ticketId}/note`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify({ note: receiptNoteInput }),
      });
      if (!res.ok) {
        window.alert('Could not save the receipt note.');
      } else {
        setEditingReceiptNote(false);
        await loadTicketDetail();
      }
    } catch (_) {
      window.alert('Could not save the receipt note.');
    }
    setSavingReceiptNote(false);
  }

  async function changeStatus(newStatusId) {
    if (!newStatusId || String(newStatusId) === String(currentStatusId)) return;
    setSavingStatus(true);
    try {
      const res = await fetch(`${API}/api/tickets/${ticketId}/status`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify({ workorderStatusID: newStatusId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        window.alert(data.error || 'Could not change the status.');
      } else {
        await loadTicketDetail();
        // Let the parent list patch this row now rather than waiting for its
        // own refetch — same contract ManagerReviewWizard already uses.
        const picked = statusMeta.find(st => String(st.workorderStatusID) === String(newStatusId));
        if (picked) onTicketUpdated?.(ticketId, { status: picked.name });
      }
    } catch (_) {
      window.alert('Could not change the status.');
    }
    setSavingStatus(false);
  }

  const saveInternalNote = async () => {
    if (!noteInput.trim()) return;
    setSavingNote(true);
    try {
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
      const timeStr = now.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' });
      const userName = currentUser?.first_name
        ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
        : (currentUser?.username || 'Staff');
      const timestamp = `[Internal Note — ${userName} — ${dateStr} ${timeStr}]`;
      const newEntry = `${timestamp}\n${noteInput.trim()}`;
      const combined = detail.internalNote?.trim()
        ? `${newEntry}\n\n${detail.internalNote.trim()}`
        : newEntry;
      await fetch(`${API}/api/tickets/${ticketId}/internal-note`, {
        method:'PUT', credentials:'include',
        headers: authHeaders(true),
        body: JSON.stringify({ note: combined }),
      });
      setDetail({ ...detail, internalNote: combined });
      setNoteInput('');
      setEditingNote(false);
    } catch (err) { console.error('Failed to save note:', err); }
    setSavingNote(false);
  };

  // ── Styles ─────────────────────────────────────────────────────────────
  const panelStyle = {
    background: isDark ? '#141d30' : '#fbfbf7',
    border:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`,
    borderRadius:10, padding:'16px 20px',
  };
  const labelStyle = { fontSize:10, color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600 };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px 16px' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ width:'100%', maxWidth:1650, background:T.bg, borderRadius:16, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,0.5)', marginBottom:24,
          height:'calc(100vh - 48px)', maxHeight:'calc(100vh - 48px)',
          display:'flex', flexDirection:'column' }}>

        {/* ── Header ── */}
        <div style={{ background: isDark ? '#161f30' : '#ebebE3', padding:'16px 24px', display:'flex', alignItems:'center', gap:16, borderBottom:`1px solid ${T.border}` }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, color:'#5a8ab8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:2 }}>Ticket Detail</div>
            <div style={{ fontSize:14, color:T.textSub, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {loading ? 'Loading…' : detail?.error ? 'Error' : `${detail?.customer?.name || ''} · ${detail?.bike?.description || ''}`}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            {/* Quote extra work moved to the Quotes screen, + Extra work (28 Oct):
                beside Send estimate it read as two ways to do one thing. */}
            {currentUser?.permissions?.quotes_approve === true && !loading && detail && !detail.error && !(detail.saleID && detail.saleID !== '0') && (
              <button onClick={() => { setEstimateSent(null); setEstimating(true); }}
                title="Send this ticket, as it is written up, to the customer to approve"
                style={{ background:'#e67e22', border:'1px solid #e67e22', borderRadius:6, color:'#fff', cursor:'pointer', height:32, padding:'0 12px', fontSize:12, fontWeight:700, fontFamily:'inherit', whiteSpace:'nowrap' }}>
                Send estimate
              </button>
            )}
            {currentUser?.permissions?.quotes !== false && currentUser?.permissions?.quotes_approve !== true && !loading && detail && !detail.error && !(detail.saleID && detail.saleID !== '0') && (
              <button onClick={() => { setRequestDone(null); setRequesting(true); }}
                title="Tell a Manager or Admin this customer needs an estimate, and what you found"
                style={{ background:'#e67e22', border:'1px solid #e67e22', borderRadius:6, color:'#fff', cursor:'pointer', height:32, padding:'0 12px', fontSize:12, fontWeight:700, fontFamily:'inherit', whiteSpace:'nowrap' }}>
                Request estimate
              </button>
            )}
            {canRemoveTickets(currentUser) && !loading && detail && !detail.error && (
              <button onClick={() => setRemoving(true)} title="Delete or archive this workorder in Lightspeed"
                style={{ background:'transparent', border:'1px solid #e74c3c', borderRadius:6, color:'#e74c3c', cursor:'pointer', height:32, padding:'0 12px', fontSize:12, fontWeight:600, marginRight:8, fontFamily:'inherit', whiteSpace:'nowrap' }}>
                Remove workorder
              </button>
            )}
            <span style={{ fontSize:12, color:T.textSub, whiteSpace:'nowrap', marginRight:4 }}>{currentIndex + 1} / {totalCount}</span>
            <button
              style={{ background:'transparent', border:`1px solid ${T.border}`, color: currentIndex === 0 ? T.textSub : T.text, borderRadius:6, width:32, height:32, fontSize:20, cursor: currentIndex === 0 ? 'default' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}
              onClick={onPrev} disabled={currentIndex === 0} title="Previous ticket (←)">‹</button>
            <button
              style={{ background:'transparent', border:`1px solid ${T.border}`, color: currentIndex === totalCount - 1 ? T.textSub : T.text, borderRadius:6, width:32, height:32, fontSize:20, cursor: currentIndex === totalCount - 1 ? 'default' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}
              onClick={onNext} disabled={currentIndex === totalCount - 1} title="Next ticket (→)">›</button>
            <button onClick={onClose} title="Close (Esc)"
              style={{ background:'transparent', border:`1px solid ${T.border}`, borderRadius:6, color:T.textSub, cursor:'pointer', width:32, height:32, fontSize:18, marginLeft:4, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}>
              ✕
            </button>
          </div>
        </div>

        {removing && (
          <RemoveTicket T={T} isDark={isDark} ticketId={ticketId}
            onClose={() => setRemoving(false)}
            onRemoved={(out) => {
              setRemoving(false);
              toast(`Ticket #${ticketId} ${out.outcome === 'archived' ? 'archived' : 'deleted'} in Lightspeed.${out.cancelledLinks?.length ? ' Unpaid payment link cancelled.' : ''}`);
              onRemoved?.(ticketId, out);
              onClose();
            }} />
        )}

        {/* ── Body ── */}
        {(!loading && (!detail || detail.error)) ? (
          <div style={{ padding:'40px 24px' }}>
            <div style={{ ...panelStyle, textAlign:'center', color:T.textSub, padding:'40px 20px' }}>Could not load ticket details.</div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', flex:1, minHeight:0 }}>

            {/* LEFT — ticket detail */}
            <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:14,
              borderRight:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`,
              overflowY:'auto', minHeight:0 }}>

              <TicketCard
                totalsPending={totalsPending}
                theme={theme}
                ticket={ticket}
                ticketDetail={detail}
                loadingDetail={loading}
                payments={payments}
          lineStock={lineStock}
          fieldEditor={{
            saveBike: saveBikeField,
            bikes: customerBikes,
            selectBike,
            saveFields: saveTicketFields,
            employeeId: detail?.techId || '',
            employees: [{ id: '', name: 'Unassigned' }, ...employees],
          }}
          receiptNoteEditor={{
            editing: editingReceiptNote,
            value: receiptNoteInput,
            setValue: setReceiptNoteInput,
            saving: savingReceiptNote,
            onOpen: () => { setReceiptNoteInput(detail?.note || ''); setEditingReceiptNote(true); },
            onCancel: () => setEditingReceiptNote(false),
            onSave: saveReceiptNote,
          }}
          statusEditor={currentUser?.permissions?.change_status === false ? null : {
            options: statusOptions,
            currentId: currentStatusId ? String(currentStatusId) : '',
            onChange: changeStatus,
            saving: savingStatus,
          }}
                flags={ticketFlags}
                onQuantityChange={updateItemQuantity}
                onDeleteLineItem={deleteLineItem}
                onEditLineItem={openLineEditor}
                addItemSlot={addItemPanel}
                internalNoteEditor={{
                  editing: editingNote,
                  onOpen: () => { setNoteInput(''); setEditingNote(true); },
                  onCancel: () => setEditingNote(false),
                  onSave: saveInternalNote,
                  noteInput,
                  setNoteInput,
                  savingNote,
                }}
              />

              {/* Time worked against labor billed, and the ticket's history in
                  Spinboard. Managers only, like the server route behind it. */}
              {currentUser?.permissions?.canViewAllTechs && detail && !detail.error && (
                <EffortPanel ticketId={ticketId} laborBilled={detail.laborBilled} T={T} isDark={isDark} />
              )}

              {/* Quotes on this ticket, and what came of each (24 Oct). */}
              {requestDone && (
                <div style={{ marginTop: 14, fontSize: 13, color: T.text, background: 'rgba(39,174,96,0.12)', border: '1px solid rgba(39,174,96,0.4)', borderRadius: 8, padding: '10px 12px' }}>
                  <strong>Estimate requested.</strong> A Manager or Admin will see it in Quotes.
                  {requestDone.lightspeed && requestDone.lightspeed.ok === false ? ' The note did not save on the Lightspeed ticket; the request still stands.' : ''}
                </div>
              )}
              <EstimateRequestBanner ticketId={ticketId} T={T} refreshKey={quotesKey}
                canDismiss={currentUser?.permissions?.quotes_approve === true}
                onSendEstimate={currentUser?.permissions?.quotes_approve === true ? () => { setEstimateSent(null); setEstimating(true); } : null} />
              {estimateSent && (
                <div style={{ marginTop: 14, fontSize: 13, color: T.text, background: 'rgba(39,174,96,0.12)', border: '1px solid rgba(39,174,96,0.4)', borderRadius: 8, padding: '10px 12px' }}>
                  <strong>Estimate {estimateSent.label} sent</strong> by {estimateSent.sent && estimateSent.sent.channel === 'email' ? 'email' : 'text'}{estimateSent.sent && estimateSent.sent.to ? ` to ${estimateSent.sent.to}` : ''}. You will see the answer in Quotes.
                </div>
              )}
              <TicketQuotes ticketId={ticketId} T={T} lineItems={detail && !detail.error ? detail.lineItems : null} refreshKey={quotesKey}
                onSendEstimate={currentUser?.permissions?.quotes_approve === true ? () => { setEstimateSent(null); setEstimating(true); } : null}
                onOpenQuote={(id) => { window.dispatchEvent(new CustomEvent('spinboard-nav', { detail: { tab: 'quotes', quoteId: id } })); if (onClose) onClose(); }} />

              {/* The customer's feedback score: on this ticket, and last time (25 Oct). */}
              <TicketFeedback ticketId={ticketId} customerId={detail && detail.customer ? detail.customer.id : null} T={T} />

              {lineEditorModal}
              {requesting && (
                <RequestEstimateDialog ticketId={ticketId} T={T} onClose={() => setRequesting(false)}
                  onDone={(d) => { setRequesting(false); setRequestDone(d); setQuotesKey(k => k + 1); }} />
              )}
              {estimating && detail && !detail.error && (
                <EstimateSend ticketId={ticketId} detail={detail} T={T} onClose={() => setEstimating(false)}
                  onSent={(q) => { setEstimating(false); setEstimateSent(q); setQuotesKey(k => k + 1); }} />
              )}
            </div>

            {/* RIGHT — SMS column (or placeholder when no phone) */}
            <div style={{ background: isDark ? '#0f1724' : '#fbfbf7',
              display:'flex', flexDirection:'column', minHeight:0, overflow:'hidden' }}>
              <div style={{ padding:'14px 18px', borderBottom:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`,
                display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:18 }}>💬</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Text Messages</div>
                  <div style={{ fontSize:12, color:T.textSub, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {detail?.customer?.name}{detail?.customer?.phone ? ` · ${fmtPhone(detail.customer.phone)}` : ''}
                  </div>
                </div>
              </div>
              <div style={{ flex:1, minHeight:0, padding:'14px 18px', display:'flex', flexDirection:'column' }}>
                {detail?.customer?.phone ? (
                  <SmsConversation
                    phone={detail.customer.phone}
                    ticketId={detail.id}
                    customerId={detail.customer.id}
                    customerName={detail.customer.name}
                    bikeLabel={detail.bike?.description || ''}
                    balance={detail.totalValue || 0}
                    ticketStatusCategory={detail.statusCategory || null}
                    theme={theme}
                    markReadOnOpen={true}
                    onSent={() => loadPayments()}
                  />
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flex:1, textAlign:'center', color:T.textSub }}>
                    <div style={{ fontSize:32, marginBottom:12, opacity:0.4 }}>📵</div>
                    <div style={{ fontSize:14, fontWeight:600, color:T.text, marginBottom:4 }}>No phone number on file</div>
                    <div style={{ fontSize:12, color:T.textSub, maxWidth:220, lineHeight:1.5 }}>
                      Add a mobile number to this customer in Lightspeed to enable texting.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TicketDetail;